/** Resolve directory speaker ids to names for suggestion and decision labels.
 *
 * Identity decisions store ids, not names, so a chip saying "Use Sharon
 * Dijksma" has to look the name up. The ids involved are only the ones the
 * document's own suggestions and overrides mention — typically a handful — so
 * fetching them individually is cheaper than pulling the whole directory, and
 * each one shares the existing `QueryKeys.speaker` cache with the rest of the
 * app. */

import { useCallback, useMemo } from 'react';
import { useQueries } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { SpeakerResponse } from '@utils/types/organization/speaker.ts';

export const useSpeakerNames = (
  speakerIds: number[],
  enabled = true
): ((speakerId: number) => string | undefined) => {
  // Stable, de-duplicated id list so the query set doesn't churn per render.
  const ids = useMemo(
    () => [...new Set(speakerIds.filter(id => Number.isFinite(id) && id > 0))],
    [speakerIds]
  );

  const results = useQueries(
    ids.map(id => ({
      queryKey: QueryKeys.speaker(id),
      queryFn: async () => {
        const { data } = await Axios.get<SpeakerResponse>(
          GET_URL(String(id), '', '', '', '').speaker
        );
        return data;
      },
      enabled,
      // Directory entries change rarely, and a stale name on a chip is far
      // better than refetching every speaker on every editor focus change.
      staleTime: 5 * 60 * 1000,
      retry: false,
      useErrorBoundary: false
    }))
  );

  const namesById = useMemo(() => {
    const map = new Map<number, string>();
    results.forEach((result, index) => {
      const speaker = (result.data as SpeakerResponse | undefined)?.result;
      if (speaker?.name) map.set(ids[index], speaker.name);
    });
    return map;
  }, [results, ids]);

  // Undefined means "not resolvable" — usually a speaker deleted from the
  // directory since the suggestion was stored. Callers show a removed state
  // rather than a blank name.
  return useCallback(
    (speakerId: number) => namesById.get(speakerId),
    [namesById]
  );
};
